import Toolbar, { Item } from "devextreme-react/toolbar";
import { useSelector } from "react-redux";
import styled from "styled-components";
import useDrawer from "../hooks/useDrawer";
import { DataGrid, Drawer } from "devextreme-react";
import AuditTrailsGrid from "./AuditTrailsGrid";
import {
  Button,
  Column,
  Editing,
  HeaderFilter,
  LoadPanel,
  Lookup,
  RequiredRule,
  RowDragging,
  Scrolling,
  Toolbar as DataGridToolbar,
  Item as DataGridItem,
} from "devextreme-react/data-grid";
import { handleCellStyle, toastMsg } from "../utils";
import { useEffect, useRef, useState } from "react";
import HtmlEditor, {
  Toolbar as HtmlEditorToolbar,
  Item as HtmlEditorItem,
} from "devextreme-react/html-editor";
import { fontValues, sizeValues } from "../data";
import { alert, custom } from "devextreme/ui/dialog";
import StudyStatusItem from "./StudyStatusItem";
import { faMinus } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { getReportDivision } from "../access";

const StudyStatusEditorWrapper = styled.div`
  width: 100%;
  font-size: 1em;
  .dx-toolbar .dx-toolbar-label {
    font-weight: bold;
  }
  .dx-button {
    margin: 0 4px;
  }
  .dx-datebox-calendar .dx-dropdowneditor-icon::before {
    content: "\f026";
  }
  .dx-cell-modified:not(.dx-datagrid-invalid) {
    background-color: transparent !important;
  }
  h3 {
    margin: 0 0 8px 0;
  }
  .ql-editor.dx-htmleditor-content {
    img {
      max-width: 1000px;
    }
  }
  .notificationItem {
    table {
      width: 100%;
      table-layout: fixed;
      word-break: break-all;
      height: auto;
      td {
        background-color: #fafafa;
        padding: 8px 16px;
        p {
          margin: 0;
        }
      }
      tr:nth-child(2n) {
        td {
          background-color: white;
        }
      }
    }
    img {
      /* display: block; */
      max-width: 100%;
      height: auto;
    }
  }
`;

const StudyStatusEditor = ({
  contrNo,
  projectListRef,
  setMode,
  studyStatusDataSource,
  yearMonth,
}) => {
  const [auditTrailsDataSource, setAuditTrailsDataSource] = useState(null);
  const [taskDataSource, setTaskDataSource] = useState(null);
  //! userInfo
  const userInfo = useSelector((state) => state.userInfo);
  const reportDiv = getReportDivision(userInfo.div);
  // const userInfo = { div: "SA", employeeNo: "23070302" };
  const { isDrawerVisible, toggleDrawer } = useDrawer();

  const actionPlanRef = useRef(null);
  const OFProjectStatusRef = useRef(null);
  const projectStatusRef = useRef(null);

  const projectStatusDataSource =
    studyStatusDataSource?.["1"]?.map((el, idx) => ({
      ...el,
      id: `projectStatus${idx}`,
    })) || [];
  const actionPlanDataSource =
    studyStatusDataSource?.["2"]?.map((el, idx) => ({
      ...el,
      id: `actionPlan${idx}`,
    })) || [];

  useEffect(() => {
    //! api GET R13. Get the description of study status (except OF div.)
    if (reportDiv !== "OF") {
      axios
        .get(
          `${process.env.REACT_APP_BECCO_API_URL}/api/mthlyReport/proj/studyStatus/descr/${reportDiv}`
        )
        .then((res) => setTaskDataSource(res.data.descr));
    }
  }, [reportDiv]);

  useEffect(() => {
    //! api GET R14. Get the audit trails of study status
    if (isDrawerVisible) {
      axios
        .get(
          `${process.env.REACT_APP_BECCO_API_URL}/api/mthlyReport/proj/studyStatus/AT/${yearMonth}/${contrNo}/${reportDiv}`
        )
        .then((res) => {
          const formattedData = res.data.reduce((res, ele) => {
            const {
              id,
              employeeNo,
              name,
              date,
              type,
              order,
              dl,
              descr,
              status,
            } = ele;

            // ID 기반으로 객체 초기화
            if (!res[id]) {
              res[id] = { id, employeeNo, name, date, [type]: [] };
            }

            // Type 기반으로 데이터 배열 초기화
            if (!res[id][type]) {
              res[id][type] = [];
            }

            // 해당 Type에 데이터 추가
            res[id][type].push({ order, dl, descr, status });

            return res;
          }, {});
          setAuditTrailsDataSource(formattedData);
        });
    }
  }, [isDrawerVisible, contrNo, reportDiv, yearMonth]);

  const handleAddRow = (ref) => {
    //* row 추가 버튼을 누르면 dataSource에 추가되고, 각 셀에 대한 validation을 진행한다.
    const newData = {
      dl: null,
      descr: null,
      status: "1",
    };

    const component = ref.current.instance;
    component
      .getDataSource()
      .store()
      .push([{ type: "insert", data: newData, index: 0 }]);

    setTimeout(() => {
      // row의 validation.
      for (let dataField of Object.keys(newData)) {
        const value = component.cellValue(0, dataField);
        component.cellValue(0, dataField, value);
      }
    }, 800);
  };

  const handleClickMode = () => {
    setMode("read");
  };

  const handleCustomItemCreate = (e) => {
    if (!e.text) {
      e.customItem = null;
      return;
    }
    const newItem = e.text;
    const dataSource = e.component.getDataSource();
    const isExistingItem = e.component
      .getDataSource()
      .items()
      .includes(newItem);
    if (isExistingItem) {
      // 이미 존재하는 데이터라면 customItem을 newItem으로 지정한다.
      e.customItem = newItem;
    } else {
      // 존재하지 않는 데이터라면 dataSource에 추가하고 customItem을 newItem으로 지정한다.
      e.customItem = dataSource
        .store()
        .insert(newItem)
        .then(() => dataSource.load())
        .then(() => newItem)
        .catch((error) => {
          throw error;
        });
    }
  };

  const handleDeleteRow = (e) => {
    const data = e.row.data;
    if (!data.id) {
      // id가 없다면 deleteRow
      e.component.deleteRow(e.row.rowIndex);
    } else {
      // 있다면 dataSource에서 remove
      e.component
        .getDataSource()
        .store()
        .push([{ type: "remove", key: data.id }]);
    }
  };

  const handleOnAdd = (e) => {
    const data = e.itemData;
    e.component
      .getDataSource()
      .store()
      .push([{ type: "insert", data, index: e.toIndex }]);

    setTimeout(() => {
      // row의 validation.
      for (let dataField of Object.keys(data)) {
        const value = e.component.cellValue(e.toIndex, dataField);
        e.component.cellValue(e.toIndex, dataField, value);
      }
    }, 800);
  };

  const handleOnRemove = (e) => {
    const data = e.itemData;
    if (!data.id) {
      // id가 없다면 deleteRow
      e.component.deleteRow(e.fromIndex);
    } else {
      // 있다면 dataSource에서 remove
      e.component
        .getDataSource()
        .store()
        .push([{ type: "remove", key: data.id }]);
    }
  };

  const handleOnReorder = (e) => {
    handleOnRemove(e);
    handleOnAdd(e);
  };

  const handleOutsideClick = (e) => {
    if (e.target.textContent !== "Audit Trails") {
      toggleDrawer(false);
      return true;
    }
  };

  const validateGridValue = (ref) => {
    const gridInstance = ref.current.instance;

    const validationState =
      gridInstance.getController("validating")._validationState;
    const errorMessages = [];

    const visibleRowKeys = gridInstance.getVisibleRows().map((row) => row.key);

    validationState.forEach((row) => {
      if (visibleRowKeys.includes(row.key) && row.validationResults) {
        const cellValidation = Object.values(row.validationResults);
        cellValidation.forEach((c) => {
          if (c.status === "invalid") {
            const rule = c.brokenRule;
            const message =
              rule.type === "required"
                ? `${c.name}은 필수 항목입니다`
                : rule.message;
            errorMessages.push(message);
          }
        });
      }
    });

    if (errorMessages.length > 0) {
      alert(errorMessages.join("<br />"), "Validation Error");
      return false;
    }
    return true;
  };

  const handleSaveStudyStatus = async () => {
    // validation
    if (reportDiv !== "OF") {
      const isValid =
        validateGridValue(projectStatusRef) && validateGridValue(actionPlanRef);
      if (!isValid) {
        // 검증이 실패한 경우에만 함수 실행을 멈춥니다.
        return;
      }
    }
    const result = await custom({
      title: "Save Confirmation",
      messageHtml: `Edit Status의 상태가 "완료"로 변경됩니다.<br/>변경하겠습니까?`,
      buttons: [
        {
          text: "완료",
          onClick: (e) => {
            return { buttonText: e.component.option("text") };
          },
        },
        {
          text: "임시저장",
          onClick: (e) => {
            return { buttonText: e.component.option("text") };
          },
        },
        {
          text: "취소",
          onClick: (e) => {
            return { buttonText: e.component.option("text") };
          },
        },
      ],
    });

    result.show().then((res) => {
      const editStatus =
        res.buttonText === "임시저장"
          ? "2"
          : res.buttonText === "완료"
          ? "3"
          : "취소";
      if (editStatus === "2" || editStatus === "3") {
        const studyStatus =
          reportDiv === "OF"
            ? [
                {
                  ...studyStatusDataSource?.[3]?.[0],
                  type: 3,
                  descr: OFProjectStatusRef.current.instance.option("value"),
                },
              ]
            : [
                ...projectStatusRef.current.instance
                  .getVisibleRows()
                  .map((el) => ({ ...el.data, type: 1 })),
                ...actionPlanRef.current.instance
                  .getVisibleRows()
                  .map((el) => ({ ...el.data, type: 2 })),
              ];
        //! api PUT U5. Update the study status
        axios
          .put(
            `${process.env.REACT_APP_BECCO_API_URL}/api/mthlyReport/proj/studyStatus/${yearMonth}/${contrNo}/${reportDiv}`,
            { studyStatus, editStatus, employeeNo: userInfo.employeeNo }
          )
          .then((response) => {
            if (!response) throw new Error("Monthly report save failed");
            setMode("read");
            toastMsg(
              `${reportDiv} 부서의 Edit Status가 ${
                editStatus === "2" ? `임시저장으로` : `완료로`
              } 변경되었습니다`,
              true
            );
            projectListRef.current.instance.refresh();
          })
          .catch((error) => {
            toastMsg(error.response?.data?.message || "월간보고를 저장하지 못했습니다.", false);
          });
      } else if (editStatus === "취소") {
        console.log("취소처리");
      }
    });
  };

  const handleStatusChange = (newData, value, currentRowData, e) => {
    const name = e.component._props.name;

    newData.status = {
      cplCheckbox: value ? "3" : "1",
      hldCheckbox: value ? "2" : "1",
    }[name];
  };

  const renderAuditTrails = () => {
    return (
      <AuditTrailsGrid
        dataSource={Object.values(auditTrailsDataSource ?? []).reverse()}
        renderAuditTrailsDetailView={renderStudyStatusDetailView}
      />
    );
  };

  const renderStatusItem = (e) => {
    if (!e) return;
    const isOldValue = e.column.caption === "Old Value";
    const isActionPlanItem = e.data.item === "Action Plan";
    const isNotification = e.data.item === "Notification";
    return (
      <div>
        {isNotification ? (
          <div
            className="notificationItem"
            dangerouslySetInnerHTML={{ __html: e.value?.[0]?.descr }}
            style={{ textAlign: "left" }}
          />
        ) : (
          e.value.map((item, idx) => (
            <StudyStatusItem
              key={idx}
              item={item}
              icon={isActionPlanItem ? faMinus : null}
              color={isActionPlanItem ? "black" : null}
              oldValue={isOldValue}
            />
          ))
        )}
      </div>
    );
  };

  const renderStudyStatusDetailView = (e) => {
    const formattedData = ["1", "2", "3"].reduce((res, type) => {
      if (e.data[type]) {
        const itemTypeMap = {
          1: "Project Status",
          2: "Action Plan",
          3: "Notification",
        };

        const item = itemTypeMap[type];
        const oldValue = auditTrailsDataSource?.[e.data.id - 1]?.[type] || [];
        const newValue = e.data[type];

        res.push({ item, oldValue, newValue });
      }
      return res;
    }, []);

    return (
      <div>
        <h3>Detail</h3>
        <DataGrid
          allowColumnResizing={true}
          columnResizingMode={"nextColumn"}
          dataSource={formattedData}
          onCellPrepared={handleCellStyle}
          showBorders={true}
          showColumnLines={true}
          wordWrapEnabled={true}
        >
          <Column
            dataField="item"
            alignment="center"
            caption="Item"
            width={100}
          />
          <Column
            dataField="oldValue"
            alignment="center"
            caption="Old Value"
            cellRender={renderStatusItem}
          />
          <Column
            dataField="newValue"
            alignment="center"
            caption="New Value"
            cellRender={renderStatusItem}
          />
        </DataGrid>
      </div>
    );
  };

  return (
    <StudyStatusEditorWrapper>
      <Toolbar>
        <Item
          name="title"
          location="before"
          text={`Study Status of ${reportDiv}`}
        />
        <Item
          name="auditTrailsButton"
          location="after"
          options={{
            onClick: toggleDrawer,
            stylingMode: "contained",
            text: "Audit Trails",
            type: "success",
          }}
          widget="dxButton"
        />
        <Item
          name="saveButton"
          location="after"
          options={{
            icon: "save",
            onClick: handleSaveStudyStatus,
            stylingMode: "contained",
            type: "danger",
            text: "Save",
          }}
          widget="dxButton"
        />
        <Item
          name="revertButton"
          location="after"
          options={{
            icon: "revert",
            onClick: handleClickMode,
            stylingMode: "contained",
            type: "normal",
          }}
          widget="dxButton"
        />
      </Toolbar>
      <Drawer
        closeOnOutsideClick={handleOutsideClick}
        height={590}
        opened={isDrawerVisible}
        openedStateMode="overlap"
        position="right"
        render={renderAuditTrails}
        revealMode="slide"
        shading={true}
        width={"100%"}
      >
        {reportDiv !== "OF" ? (
          <div style={{ display: "flex" }}>
            <DataGrid
              allowColumnResizing={true}
              columnResizingMode={"nextColumn"}
              dataSource={projectStatusDataSource}
              height={570}
              id="projectStatusGrid"
              keyExpr={"id"}
              onCellPrepared={handleCellStyle}
              ref={projectStatusRef}
              showBorders={true}
              showColumnLines={true}
              width={"50%"}
              wordWrapEnabled={true}
            >
              <Editing
                allowAdding={true}
                allowDeleting={true}
                allowUpdating={true}
                mode="batch"
              />
              <HeaderFilter visible={true} />
              <LoadPanel enabled={true} />
              <RowDragging
                allowReordering={true}
                group="studyStatusGroup"
                onAdd={handleOnAdd}
                onRemove={handleOnRemove}
                onReorder={handleOnReorder}
              />
              <Scrolling mode="virtual" showScrollbar={"always"} />
              <DataGridToolbar>
                <DataGridItem
                  name="gridTitle"
                  location="before"
                  text="Project Status"
                />
                <DataGridItem
                  name="addRowBtn"
                  location="after"
                  options={{
                    icon: "add",
                    onClick: () => handleAddRow(projectStatusRef),
                  }}
                  widget="dxButton"
                />
              </DataGridToolbar>
              <Column
                dataField="dl"
                alignment="center"
                caption="Due Date"
                dataType="date"
                editorOptions={{
                  useMaskBehavior: true,
                  dateSerializationFormat: "yyyy-MM-dd",
                }}
                format="yyyy-MM-dd"
                showEditorAlways={true}
                width={120}
              />
              <Column
                dataField="descr"
                alignment="center"
                caption="Description"
                editorOptions={{
                  acceptCustomValue: true,
                  onCustomItemCreating: handleCustomItemCreate,
                  searchEnabled: true,
                  wrapItemText: true,
                }}
                showEditorAlways={true}
              >
                <Lookup dataSource={taskDataSource} />
                <RequiredRule message="" />
              </Column>
              <Column
                dataField="completed"
                alignement="center"
                caption="Completed" // status === "3"
                calculateCellValue={(e) => String(e.status) === "3"}
                dataType="boolean"
                editorOptions={{ name: "cplCheckbox" }}
                setCellValue={handleStatusChange}
                showEditorAlways={true}
                width={130}
              />
              <Column
                dataField="holding"
                alignment="center"
                caption="Holding" // status === "2"
                calculateCellValue={(e) => String(e.status) === "2"}
                dataType="boolean"
                editorOptions={{ name: "hldCheckbox" }}
                setCellValue={handleStatusChange}
                showEditorAlways={true}
                width={110}
              />
              <Column type="buttons" width={50}>
                <Button icon="trash" onClick={handleDeleteRow} />
              </Column>
            </DataGrid>
            <div style={{ width: "16px" }} />
            <DataGrid
              allowColumnResizing={true}
              columnResizingMode={"nextColumn"}
              dataSource={actionPlanDataSource}
              height={570}
              id="actionPlanGrid"
              keyExpr={"id"}
              onCellPrepared={handleCellStyle}
              ref={actionPlanRef}
              showBorders={true}
              showColumnLines={true}
              width={"50%"}
              wordWrapEnabled={true}
            >
              <Editing
                allowAdding={true}
                allowDeleting={true}
                allowUpdating={true}
                mode="batch"
              />
              <HeaderFilter visible={true} />
              <LoadPanel enabled={true} />
              <RowDragging
                allowReordering={true}
                group="studyStatusGroup"
                onAdd={handleOnAdd}
                onRemove={handleOnRemove}
                onReorder={handleOnReorder}
              />
              <Scrolling mode="virtual" showScrollbar={"always"} />
              <DataGridToolbar>
                <DataGridItem
                  name="gridTitle"
                  location="before"
                  text="Action Plan"
                />
                <DataGridItem
                  name="addRowBtn"
                  location="after"
                  options={{
                    icon: "add",
                    onClick: () => handleAddRow(actionPlanRef),
                  }}
                  widget="dxButton"
                />
              </DataGridToolbar>
              <Column
                dataField="dl"
                alignment="center"
                caption="Due Date"
                dataType="date"
                editorOptions={{
                  useMaskBehavior: true,
                  dateSerializationFormat: "yyyy-MM-dd",
                }}
                format="yyyy-MM-dd"
                showEditorAlways={true}
                width={120}
              />
              <Column
                dataField="descr"
                alignment="center"
                caption="Description"
                editorOptions={{
                  acceptCustomValue: true,
                  onCustomItemCreating: handleCustomItemCreate,
                  searchEnabled: true,
                  wrapItemText: true,
                }}
                showEditorAlways={true}
              >
                <Lookup dataSource={taskDataSource} />
                <RequiredRule message="" />
              </Column>
              <Column type="buttons" width={50}>
                <Button icon="trash" onClick={handleDeleteRow} />
              </Column>
            </DataGrid>
          </div>
        ) : (
          <div>
            <h3>Notification</h3>
            <HtmlEditor
              defaultValue={studyStatusDataSource?.[3]?.[0].descr}
              height={550}
              id="OFProjectStatusEditor"
              ref={OFProjectStatusRef}
              width={"100%"}
            >
              <HtmlEditorToolbar multiline={true}>
                <HtmlEditorItem name="undo" />
                <HtmlEditorItem name="redo" />
                <HtmlEditorItem name="separator" />
                <HtmlEditorItem name="size" acceptedValues={sizeValues} />
                <HtmlEditorItem name="font" acceptedValues={fontValues} />
                <HtmlEditorItem name="separator" />
                <HtmlEditorItem name="bold" />
                <HtmlEditorItem name="italic" />
                <HtmlEditorItem name="strike" />
                <HtmlEditorItem name="underline" />
                <HtmlEditorItem name="separator" />
                <HtmlEditorItem name="alignLeft" />
                <HtmlEditorItem name="alignCenter" />
                <HtmlEditorItem name="alignRight" />
                <HtmlEditorItem name="alignJustify" />
                <HtmlEditorItem name="separator" />
                <HtmlEditorItem name="orderedList" />
                <HtmlEditorItem name="bulletList" />
                <HtmlEditorItem name="separator" />
                <HtmlEditorItem name="color" />
                <HtmlEditorItem name="background" />
                <HtmlEditorItem name="separator" />
                <HtmlEditorItem name="link" />
                <HtmlEditorItem name="clear" />
              </HtmlEditorToolbar>
            </HtmlEditor>
          </div>
        )}
      </Drawer>
    </StudyStatusEditorWrapper>
  );
};

export default StudyStatusEditor;
